// AI 사용량 화면의 표시 규칙 — 라벨·숫자·KST 기간·모델 분류를 한곳에 모아 오독을 막는다
import type { UsageModel, UsageUser } from '../../api/adminUsage';

/* ────────────────────────────────────────────────────────────────
 * 라벨 — requestCount를 "대화 수"로 부르지 않기 위한 단일 출처
 * ──────────────────────────────────────────────────────────────── */

/**
 * requestCount의 표시명. 이 값은 채팅 수가 아니라 LLM 호출 수다.
 * 채팅 한 번이 보통 3~4회 호출을 만든다(라우팅·답변 생성·요약·개인화 추출,
 * 사내 문서 검색 시 질의 임베딩, 도구 사용 시 추가).
 */
export const REQUEST_COUNT_LABEL = 'AI 호출 수';
export const REQUEST_COUNT_HINT = '채팅 1회가 보통 3~4회 호출을 만듭니다';

/* ────────────────────────────────────────────────────────────────
 * 숫자·시간
 * ──────────────────────────────────────────────────────────────── */

/** 서버가 자르는 기준 타임존 — 브라우저 로컬 타임존을 쓰면 하루가 어긋난다 */
const KST = 'Asia/Seoul';

/** 천 단위 구분 기호 — 숫자 열은 CSS의 tabular-nums와 함께 쓴다 */
export function formatNumber(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('ko-KR') : '-';
}

/** 축 눈금용 축약 표기 — 정확한 값은 툴팁·표에서 천 단위 구분 기호로 보여준다 */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000000) return `${Math.round(value / 100000) / 10}M`;
  if (abs >= 1000) return `${Math.round(value / 100) / 10}K`;
  return String(value);
}

/** 비율 표기 — 0.4% 미만의 값도 0%로 뭉개지 않게 소수 한 자리까지 본다 */
export function formatPercent(part: number, total: number): string {
  if (total <= 0) return '0%';
  const ratio = (part / total) * 100;
  if (ratio > 0 && ratio < 0.1) return '<0.1%';
  return `${ratio >= 10 ? Math.round(ratio) : Math.round(ratio * 10) / 10}%`;
}

/** ISO 8601(+09:00) → "n분 전". 하루가 넘으면 날짜로 떨어뜨린다 */
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return '-';
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return '-';

  const diffSec = Math.floor((Date.now() - at) / 1000);
  if (diffSec < 0) return '방금 전';
  if (diffSec < 60) return '방금 전';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}분 전`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}시간 전`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}일 전`;
  return formatDateTime(iso).slice(0, 10);
}

/** 상대 시간 옆에 붙일 정확한 시각 (KST) — title 속성으로 노출한다 */
export function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '-';
  // en-CA는 yyyy-MM-dd 형식이라 문자열 가공 없이 그대로 쓸 수 있다
  const day = date.toLocaleDateString('en-CA', { timeZone: KST });
  const time = date.toLocaleTimeString('en-GB', { timeZone: KST, hour: '2-digit', minute: '2-digit' });
  return `${day} ${time}`;
}

/* ────────────────────────────────────────────────────────────────
 * 기간 — 서버가 KST로 자르므로 브라우저 타임존을 믿지 않는다
 * ──────────────────────────────────────────────────────────────── */

/** 오늘(KST) yyyy-MM-dd */
export function todayInKst(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: KST });
}

/**
 * yyyy-MM-dd에 일 수를 더한다.
 * 문자열을 UTC 자정으로 파싱해 계산한다 — 로컬 타임존이 개입하지 않고,
 * KST는 DST가 없어 날짜 경계가 어긋나지 않는다.
 */
export function addDays(day: string, delta: number): string {
  const base = new Date(`${day}T00:00:00Z`).getTime();
  return new Date(base + delta * 86400000).toISOString().slice(0, 10);
}

/** 기간 프리셋 — to는 그날을 포함하므로 7일이면 오늘 포함 7일이다 */
export const RANGE_PRESETS: { days: number; label: string }[] = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
];

export function presetRange(days: number): { from: string; to: string } {
  const to = todayInKst();
  return { from: addDays(to, -(days - 1)), to };
}

/** 프리셋과 일치하는 기간이면 그 일 수를, 아니면 null (직접 지정 표시용) */
export function matchedPreset(from: string, to: string): number | null {
  for (let i = 0; i < RANGE_PRESETS.length; i += 1) {
    const preset = presetRange(RANGE_PRESETS[i].days);
    if (preset.from === from && preset.to === to) return RANGE_PRESETS[i].days;
  }
  return null;
}

/** 조회 가능한 기간인지 — 시작이 종료보다 뒤면 서버에 보내지 않는다 */
export function isValidRange(from: string, to: string): boolean {
  return !!from && !!to && from <= to;
}

/** x축 라벨 — yyyy-MM-dd에서 MM/DD만 */
export function formatDayLabel(day: string): string {
  return day.length >= 10 ? `${day.slice(5, 7)}/${day.slice(8, 10)}` : day;
}

/* ────────────────────────────────────────────────────────────────
 * 호출 종류 분류
 * ──────────────────────────────────────────────────────────────── */

/**
 * chat 안에서 본 답변과 보조 작업을 가르는 모델 접두사.
 * 모델명 뒤에 붙는 날짜는 실제 버전이라 바뀌므로 접두사만 비교한다.
 */
const MAIN_MODEL_PREFIX = 'gpt-5.6-terra';
const ASSIST_MODEL_PREFIX = 'gpt-5-mini';

export type UsageKind = 'main' | 'assist' | 'other' | 'embedding';

export const KIND_LABEL: Record<UsageKind, string> = {
  main: '본 답변',
  assist: '보조 호출',
  other: '기타 호출',
  embedding: '임베딩',
};

export const KIND_DESC: Record<UsageKind, string> = {
  main: '사용자에게 보이는 답변 생성',
  assist: '라우팅·요약·개인화 추출 등 내부 작업',
  other: '분류에 없는 모델 — 확인이 필요합니다',
  embedding: '사내 문서를 찾기 위한 질의 벡터화',
};

/** 입력/출력 토큰 색 — 일별 추이 차트의 두 시리즈 */
export const TOKEN_COLOR = { prompt: '#2a78d6', completion: '#eb6834' };

/** 차트·범례 색 — 값의 크기가 아니라 갈래를 나타내므로 고정 배정이다 */
export const KIND_COLOR: Record<UsageKind, string> = {
  main: '#2a78d6',
  assist: '#eda100',
  other: '#e34948',
  embedding: '#64748b',
};

/**
 * 호출 종류 판정.
 * 대화 생성/임베딩 구분은 operation으로만 한다 — 모델명으로 판별하면 모델이 바뀔 때 틀린다.
 * 본 답변/보조 구분에서만 모델 접두사를 쓰고, 어느 쪽도 아닌 chat 모델은 '보조'로
 * 뭉개지 않고 '기타'로 뺀다 (새 본 답변 모델이 보조로 오분류되면 수치가 조용히 틀어진다).
 */
export function classifyUsage(entry: UsageModel): UsageKind {
  if (entry.operation === 'embedding') return 'embedding';
  if (entry.operation !== 'chat') return 'other';
  const model = entry.model || '';
  if (model.startsWith(MAIN_MODEL_PREFIX)) return 'main';
  if (model.startsWith(ASSIST_MODEL_PREFIX)) return 'assist';
  return 'other';
}

export interface UsageKindGroup {
  kind: UsageKind;
  label: string;
  totalTokens: number;
  requestCount: number;
  models: UsageModel[];
}

const KIND_ORDER: UsageKind[] = ['main', 'assist', 'other', 'embedding'];

/** models[]를 네 갈래로 합산 — 항목이 하나도 없는 갈래는 반환하지 않는다 */
export function groupByKind(models: UsageModel[]): UsageKindGroup[] {
  const buckets: Record<string, UsageKindGroup> = {};
  KIND_ORDER.forEach((kind) => {
    buckets[kind] = { kind, label: KIND_LABEL[kind], totalTokens: 0, requestCount: 0, models: [] };
  });

  models.forEach((entry) => {
    const bucket = buckets[classifyUsage(entry)];
    bucket.totalTokens += entry.totalTokens;
    bucket.requestCount += entry.requestCount;
    bucket.models.push(entry);
  });

  return KIND_ORDER.map((kind) => buckets[kind]).filter((group) => group.models.length > 0);
}

/* ────────────────────────────────────────────────────────────────
 * 사용자 표기
 * ──────────────────────────────────────────────────────────────── */

/** 사용자 특정에 실패한 호출의 합계 행 — 숨기지 않고 그대로 보여준다 */
export const SYSTEM_USER_ID = '(system)';

export function isSystemUser(user: Pick<UsageUser, 'userId'>): boolean {
  return user.userId === SYSTEM_USER_ID;
}

/** 사번이 없을 때 행을 식별할 수 있게 userId를 축약한다 */
export function shortUserId(userId: string): string {
  return userId.length > 12 ? `${userId.slice(0, 8)}…` : userId;
}

/** 부서 표기 — 아직 채팅 이력이 없으면 null이 온다 */
export function displayDept(deptCode: string | null): string {
  return deptCode || '미확인';
}

export interface UserLabel {
  /** 사번 자리 — username이 없으면 '미확인' */
  idNo: string;
  /** 사번이 없을 때 행을 가리키는 축약 userId (있으면 빈 문자열) */
  shortId: string;
  dept: string;
}

/** 랭킹 표와 상세 패널이 같은 규칙으로 사람을 표기하도록 한곳에서 만든다 */
export function userLabel(user: Pick<UsageUser, 'userId' | 'username' | 'deptCode'>): UserLabel {
  const dept = displayDept(user.deptCode);
  // (system)은 사용자 특정에 실패한 호출의 합계다 — 사번 자리에 그대로 둔다
  if (user.userId === SYSTEM_USER_ID) return { idNo: SYSTEM_USER_ID, shortId: '', dept };
  return {
    idNo: user.username || '미확인',
    shortId: user.username ? '' : shortUserId(user.userId),
    dept,
  };
}
